import { deleteAccessToken, removeCookie, setAccessToken, setCookie } from '@backrs_ui/common-ui';

import { ENVIRONMENT } from '../settings';

export const IS_SANDBOX = ENVIRONMENT === 'sandbox';

/** The accounts a visitor may enter the sandbox as. */
export const SANDBOX_PERSONA = {
  coach: 'coach',
  admin: 'admin',
} as const;

export type SandboxPersona = (typeof SANDBOX_PERSONA)[keyof typeof SANDBOX_PERSONA];

const PERSONA_PARAM = 'as';
const PERSONA_KEY = 'sandbox_persona';

const isSandboxPersona = (value: string | null | undefined): value is SandboxPersona =>
  Object.values(SANDBOX_PERSONA).includes(value as SandboxPersona);

/** The account this link asks to be signed in as, or null when it names none. */
export const requestedSandboxPersona = (search: string): SandboxPersona | null => {
  const requested = new URLSearchParams(search).get(PERSONA_PARAM)?.trim().toLowerCase();

  return isSandboxPersona(requested) ? requested : null;
};

/** Drops an account request from the address bar once it has been answered. */
export const clearRequestedSandboxPersona = (): void => {
  const url = new URL(window.location.href);

  if (!url.searchParams.has(PERSONA_PARAM)) return;

  url.searchParams.delete(PERSONA_PARAM);
  window.history.replaceState(null, '', url);
};

/**
 * The account the session in hand belongs to, or null when nothing recorded one — a session can
 * outlive the record, since the token also survives in a cookie.
 */
export const storedSandboxPersona = (): SandboxPersona | null => {
  const stored = localStorage.getItem(PERSONA_KEY);

  return isSandboxPersona(stored) ? stored : null;
};

// Survives the nightly rebuild that deletes the visitor's account, so analytics can still tell a
// returning prospect from a new one.
const VISITOR_ID_COOKIE = 'sandbox_visitor_id';
const VISITOR_ID_TTL_DAYS = 365;

type SandboxSessionResponse = Readonly<{
  token: string;
}>;

/**
 * Creates a private copy of the demo school and returns an access token for the coach `persona`
 * names within it, or null when no copy can be served. The token stops resolving once the nightly
 * rebuild replaces that copy.
 */
export const mintSandboxSession = async (persona: SandboxPersona): Promise<string | null> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_BASEURL}/sandbox/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ persona }),
    });

    if (!response.ok) {
      const failure = (await response.json().catch(() => null)) as {
        error?: string;
        message?: string;
      } | null;

      console.error('Failed to mint sandbox session', {
        persona,
        status: response.status,
        reason: failure?.message ?? failure?.error,
      });

      return null;
    }

    const data = (await response.json()) as Partial<SandboxSessionResponse>;

    if (typeof data?.token !== 'string') {
      console.error('Sandbox session response missing token', { persona });

      return null;
    }

    setAccessToken('backrs_ui_token', data.token);
    setCookie(data.token, ENVIRONMENT);
    localStorage.setItem(PERSONA_KEY, persona);

    return data.token;
  } catch (error) {
    console.error('Failed to mint sandbox session', { persona }, error);

    return null;
  }
};

/**
 * Whether the API has actually rejected the token, as opposed to being unable to answer. Only a 401
 * means the account behind it is gone; anything else leaves a working session working. Errs towards
 * keeping the session, since replacing one costs the visitor their copy and its history.
 */
export const isSandboxTokenRejected = async (token: string): Promise<boolean> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_BASEURL}/users/auto-login`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response.status === 401;
  } catch (error) {
    console.error('Failed to check whether the sandbox token still resolves', error);

    return false;
  }
};

/**
 * Drops a session the visitor is no longer to be signed in under, and hands back the account it
 * belonged to so a replacement can be minted for the same one.
 */
export const clearSandboxSession = (): SandboxPersona | null => {
  const dropped = storedSandboxPersona();

  deleteAccessToken('backrs_ui_token');
  removeCookie();
  localStorage.removeItem(PERSONA_KEY);

  return dropped;
};

// Held for the page's lifetime so a cookie that never lands still yields one id per visit rather
// than a new one on every read.
let visitorId: string | null = null;

export const getSandboxVisitorId = (): string => {
  if (visitorId) return visitorId;

  const stored = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${VISITOR_ID_COOKIE}=`))
    ?.split('=')[1];

  if (stored) {
    visitorId = decodeURIComponent(stored);

    return visitorId;
  }

  visitorId = crypto.randomUUID();
  const expires = new Date(Date.now() + VISITOR_ID_TTL_DAYS * 24 * 60 * 60 * 1000);

  document.cookie = `${VISITOR_ID_COOKIE}=${encodeURIComponent(visitorId)}; path=/; expires=${expires.toUTCString()}; secure`;

  return visitorId;
};
